/**
 * Artifact AI 수정 서비스
 * 텍스트 선택 수정 기능을 위한 API 호출 함수
 */
import { apiJson, isQuotaExceededError } from "./apiClient";

/**
 * 백엔드/모델이 "JSON 문자열"을 반환하는 경우가 있어 정규화한다.
 * - 정상: { success: true, original: "...", modified: "..." }
 * - 비정상(문자열): modified가 '{"original":"...","modified":"..."}' 같은 JSON 텍스트
 * - 레거시: modified_text 키로 내려오는 경우
 */
const normalizeAiModifyResult = (result, fallbackOriginal = "") => {
  if (!result || typeof result !== "object") return result;

  const normalized = { ...result };

  // 레거시 키 호환
  if (normalized.modified == null && typeof normalized.modified_text === "string") {
    normalized.modified = normalized.modified_text;
  }
  if (normalized.original == null && typeof normalized.original_text === "string") {
    normalized.original = normalized.original_text;
  }

  // modified가 JSON 텍스트면 파싱해서 modified/original만 꺼낸다.
  if (typeof normalized.modified === "string") {
    let raw = normalized.modified.trim();

    // 코드펜스(```json ... ```)가 섞여 나오는 경우 방어
    raw = raw.replace(/^```(?:json)?\s*/i, "").replace(/\s*```$/i, "").trim();

    const looksJson =
      raw.startsWith("{") && (raw.includes('"modified"') || raw.includes("'modified'"));

    const tryParseJson = (s) => {
      try {
        return JSON.parse(s);
      } catch (e) {
        return null;
      }
    };

    if (looksJson) {
      let parsed = tryParseJson(raw);
      if (!parsed) {
        const start = raw.indexOf("{");
        const end = raw.lastIndexOf("}");
        if (start !== -1 && end !== -1 && end > start) {
          parsed = tryParseJson(raw.slice(start, end + 1));
        }
      }

      if (parsed && typeof parsed === "object") {
        if (typeof parsed.modified === "string") normalized.modified = parsed.modified;
        if (typeof parsed.original === "string") normalized.original = parsed.original;
      }
    }
  }

  if (!normalized.original && typeof fallbackOriginal === "string") {
    normalized.original = fallbackOriginal;
  }

  return normalized;
};

/**
 * 아티팩트 텍스트를 AI로 수정
 * @param {number} artifactId - 아티팩트 ID
 * @param {string} selectedText - 선택된 텍스트
 * @param {string} modificationPrompt - 사용자 요청 프롬프트
 * @param {string} fullContext - 전체 문서 내용 (옵션)
 * @returns {Promise<{success: boolean, original?: string, modified?: string, error?: string}>}
 */
export const modifyArtifactText = async (
  artifactId,
  selectedText,
  modificationPrompt,
  fullContext = '',
  options = {}
) => {
  try {
    const result = await apiJson(`/artifacts/${artifactId}/modify`, {
      method: 'POST',
      body: JSON.stringify({
        selected_text: selectedText,
        // 신규 명칭(user_prompt) + 기존 명칭(modification_prompt) 둘 다 지원
        user_prompt: modificationPrompt,
        modification_prompt: modificationPrompt,
        full_context: fullContext,
        selected_markdown_hint: options?.selected_markdown_hint || '',
      }),
    });
    return normalizeAiModifyResult(result, selectedText);
  } catch (error) {
    console.error('❌ modifyArtifactText 오류:', error);
    if (isQuotaExceededError(error)) {
      throw error;
    }
    return {
      success: false,
      error: error.message || '텍스트 수정 중 오류가 발생했습니다.',
    };
  }
};
